import { Info, ShoppingBag } from 'lucide-react';
import {
  formatCreatedDate,
  formatStatusLabel,
  isStatusCompleted,
} from '../../lib/projectWorkspaceUtils';
import { getProjectToolType } from '../../lib/projectToolType';
import { formatGardenSelectionSummary } from '../../lib/gardenDesignOptions';

/**
 * @param {{
 *   project: Record<string, unknown>;
 *   shopItemCount?: number;
 * }} props
 */
export default function ProjectDetailsPanel({ project, shopItemCount = 0 }) {
  const createdLabel = formatCreatedDate(project.createdAt);
  const statusLabel = formatStatusLabel(project.status);
  const completed = isStatusCompleted(project.status);
  const toolType = getProjectToolType(project);
  const isExterior = toolType === 'building_exterior_design' || project.designTool === 'building-exterior';
  const isGarden = toolType === 'garden_design' || project.designTool === 'garden-design';
  const isFloorPlan =
    toolType === 'floorplan_2d_to_3d' || toolType === 'floorplan_to_room_image';

  return (
    <article className="project-result-card project-result-details-card">
      <h2 className="project-result-card-title project-result-card-title--purple">
        <Info size={14} strokeWidth={2} aria-hidden />
        Design Details
      </h2>
      <ul className="project-result-details-rows">
        <li className="project-result-details-row">
          <span className="project-result-details-label">Status</span>
          <span
            className={`project-result-details-value${
              completed ? ' project-result-details-value--completed' : ''
            }`}
          >
            {statusLabel || '—'}
          </span>
        </li>
        {isExterior ? (
          <>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Exterior change</span>
              <span className="project-result-details-value">
                {String(project.exteriorChangeLevel || '—')}
              </span>
            </li>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Facade material</span>
              <span className="project-result-details-value">
                {String(project.facadeMaterial || '—')}
              </span>
            </li>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Time</span>
              <span className="project-result-details-value">
                {String(project.timeOfDay || '—')}
              </span>
            </li>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Weather</span>
              <span className="project-result-details-value">
                {String(project.weather || '—')}
              </span>
            </li>
          </>
        ) : isGarden ? (
          <>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Garden change</span>
              <span className="project-result-details-value">
                {String(project.gardenChangeLevel || '—')}
              </span>
            </li>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Natural elements</span>
              <span className="project-result-details-value">
                {formatGardenSelectionSummary(
                  Array.isArray(project.naturalElements) ? project.naturalElements : [],
                )}
              </span>
            </li>
            <li className="project-result-details-row">
              <span className="project-result-details-label">Structures</span>
              <span className="project-result-details-value">
                {formatGardenSelectionSummary(
                  Array.isArray(project.manMadeStructures) ? project.manMadeStructures : [],
                )}
              </span>
            </li>
          </>
        ) : isFloorPlan ? (
          <>
            {project.outputType ? (
              <li className="project-result-details-row">
                <span className="project-result-details-label">Output type</span>
                <span className="project-result-details-value">
                  {String(project.outputType)}
                </span>
              </li>
            ) : null}
            {project.roomLabel || project.selectedRoomLabel ? (
              <li className="project-result-details-row">
                <span className="project-result-details-label">Room</span>
                <span className="project-result-details-value">
                  {String(project.roomLabel || project.selectedRoomLabel)}
                </span>
              </li>
            ) : null}
          </>
        ) : (
          <li className="project-result-details-row">
            <span className="project-result-details-label">Room Type</span>
            <span className="project-result-details-value">{String(project.roomType || '—')}</span>
          </li>
        )}
        <li className="project-result-details-row">
          <span className="project-result-details-label">Style</span>
          <span className="project-result-details-value">{String(project.style || '—')}</span>
        </li>
        {project.notes ? (
          <li className="project-result-details-row">
            <span className="project-result-details-label">Notes</span>
            <span className="project-result-details-value project-result-details-value--notes">
              {String(project.notes)}
            </span>
          </li>
        ) : null}
        <li className="project-result-details-row">
          <span className="project-result-details-label">Created</span>
          <span className="project-result-details-value">{createdLabel || '—'}</span>
        </li>
      </ul>
      {shopItemCount > 0 ? (
        <div className="project-result-shop-hint">
          <ShoppingBag size={14} strokeWidth={2} aria-hidden />
          <p>
            We detected {shopItemCount} furniture &amp; decor items in this design. Shop similar items
            to recreate this look.
          </p>
        </div>
      ) : null}
    </article>
  );
}
